<script lang="ts">
import { computed, ref, useId, useTemplateRef, watch } from 'vue';

import { debounce, isEqual } from '@sg-core/resources/assets/js/utils';
import '@sg-core/src/View/Components/Atoms/VisuallyHidden/visually-hidden.css';

import PhxsgAutocompleteDropdown from './phxsg-autocomplete-dropdown.vue';
import PhxsgAutocompleteField from './phxsg-autocomplete-field.vue';

import type { PhxsgAutocompleteFieldProps } from './phxsg-autocomplete-field.vue';
import type { PhxsgAutocompleteOption } from './phxsg-autocomplete-options.vue';
import type { HTMLAttributes } from 'vue';

export interface PhxsgAutocompleteProps {
  alwaysDisplayClearButton?: boolean;
  ariaDescribedby?: string;
  autoFocusFirstOption?: boolean;
  buttonClass?: HTMLAttributes['class'];
  clearButtonIcon?: string | false;
  clearButtonLabel?: string;
  closeOnInputBlur?: boolean;
  displayBothButtons?: boolean;
  error?: string;
  formFieldClass?: HTMLAttributes['class'];
  id?: string;
  label?: string;
  labelClass?: HTMLAttributes['class'];
  helpClass?: HTMLAttributes['class'];
  hideLabel?: boolean;
  inputClass?: HTMLAttributes['class'];
  startIcon?: string;
  loading?: boolean;
  loadingText?: string;
  minCharacters?: number;
  maxRows?: number;
  minRows?: number;
  name: string;
  noResultText?: string;
  openButtonIcon?: string | false;
  openButtonLabel?: string;
  options?: PhxsgAutocompleteOption[];
  placeholder?: string;
  teleportTarget?: string | null;
  dropdownOffset?: number;
  disableDropdown?: boolean;
}

export interface ExposedPhxsgAutocompleteMethod {
  setValue: (value: string) => void;
}
</script>

<script setup lang="ts">
const {
  alwaysDisplayClearButton = false,
  ariaDescribedby,
  autoFocusFirstOption = true,
  buttonClass,
  clearButtonIcon,
  clearButtonLabel = 'Clear current selection',
  closeOnInputBlur: closeOnInputBlurProp = true,
  displayBothButtons = false,
  error = '',
  formFieldClass,
  id,
  label,
  labelClass,
  helpClass,
  hideLabel = false,
  inputClass,
  startIcon,
  loading = false,
  loadingText = 'Loading',
  minCharacters,
  maxRows = 5,
  minRows = 1,
  name,
  noResultText = 'There isn‘t any result for your search',
  openButtonIcon,
  openButtonLabel = 'Open field‘s options',
  options = [],
  placeholder = '',
  teleportTarget = null,
  dropdownOffset = 4,
  disableDropdown = false,
} = defineProps<PhxsgAutocompleteProps>();

const emit = defineEmits<{
  'autocomplete:query': [query: string];
  'autocomplete:change': [option: PhxsgAutocompleteOption | null];
  'autocomplete:option-action': [option: PhxsgAutocompleteOption];
  'autocomplete:press-enter': [query: string];
  'autocomplete:blur': [query: string];
}>();

const currentOption = ref('');
const open = ref(false);
const query = ref('');
const rootRef = useTemplateRef<HTMLElement>('rootRef');
const selected = ref<PhxsgAutocompleteOption | null>(null);
const closeOnInputBlur = ref(closeOnInputBlurProp);
const generatedId = useId();

const inputId = computed(() => id ?? generatedId);

const shouldShowDropdown = computed(() => {
  if (disableDropdown) {
    return false;
  }

  if (minCharacters) {
    return query.value.length >= minCharacters;
  }

  return true;
});

const dropdownId = computed(() => `autocomplete-${inputId.value}__dropdown`);

const isDropdownOpen = computed(() => open.value && shouldShowDropdown.value);

const currentIndex = computed(() => options.findIndex(({ key }) => key === currentOption.value));

const activeDescendant = computed(() =>
  isDropdownOpen.value && currentIndex.value !== -1 ? `${dropdownId.value}__option--${currentIndex.value}` : undefined,
);

const controlsId = computed(() => (isDropdownOpen.value ? dropdownId.value : undefined));

const hasSelection = computed(() => !!selected.value?.value);

const hasValue = computed(() => !!query.value);

const rootClasses = computed(() => [
  'phxsg-autocomplete',
  {
    'phxsg-autocomplete--with-icon': startIcon,
    'phxsg-autocomplete--both-buttons': displayBothButtons,
  },
]);

const fieldProps = computed<PhxsgAutocompleteFieldProps>(() => {
  const errorId = `${inputId.value}-error`;

  return {
    formField: {
      class: formFieldClass,
    },
    labelAttrs: {
      text: label,
      class: [labelClass, { 'phxsg-visually-hidden': hideLabel }],
    },
    hasSelection: hasSelection.value,
    inputAttrs: {
      class: inputClass,
      id: inputId.value,
      type: 'search',
      autocomplete: 'off',
      name,
      value: query.value,
      role: 'combobox',
      placeholder,
      'aria-describedby': [ariaDescribedby, error ? errorId : undefined].filter(Boolean).join(' ') || undefined,
      'aria-autocomplete': 'list',
      'aria-expanded': isDropdownOpen.value,
      'aria-controls': controlsId.value,
      'aria-activedescendant': activeDescendant.value,
    },
    helpAttrs: {
      id: errorId,
      class: helpClass,
    },
    inputEvents: {
      onClick: onInputClick,
      onFocus: onInputFocus,
      onBlur: onInputBlur,
      onInput: onInputInput,
      onKeydown: onInputKeyDown,
    },
    startIconAttrs: {
      id: startIcon,
      size: 'sm',
    },
    buttonAttrs: {
      class: buttonClass,
      'aria-controls': hasSelection.value ? undefined : controlsId.value,
      'aria-expanded': hasSelection.value || disableDropdown ? undefined : isDropdownOpen.value,
      'aria-label': hasSelection.value ? clearButtonLabel : openButtonLabel,
      tabindex: hasSelection.value || hasValue.value ? 0 : -1,
    },
    buttonEvents: {
      onClick: onButtonClick,
      onBlur: onButtonBlur,
      onMousedown: onButtonMouseDown,
      onKeydown: onButtonKeydown,
    },
  };
});

function onOptionSelect(option: PhxsgAutocompleteOption): void {
  selected.value = option;
  query.value = option.value;
  open.value = false;
}

function onOptionAction(option: PhxsgAutocompleteOption): void {
  emit('autocomplete:option-action', option);
}

function onPressEnter(q: string): void {
  emit('autocomplete:press-enter', q);
}

const openDropdown = debounce((state: boolean) => {
  if (disableDropdown) return;
  open.value = state;
}, 25);

function onButtonMouseDown(): void {
  if (document.activeElement?.id === inputId.value && !hasSelection.value) {
    closeOnInputBlur.value = false;
  }
}

function onButtonBlur(): void {
  openDropdown(false);
}

function getButtonType(target: HTMLElement): 'clear' | 'open' | undefined {
  const button = target.closest('button');

  if (button?.classList.contains('phxsg-autocomplete__button--clear')) {
    return 'clear';
  }

  if (button?.classList.contains('phxsg-autocomplete__button--open')) {
    return 'open';
  }

  return undefined;
}

function onButtonClick(e: MouseEvent): void {
  e.preventDefault();
  const buttonType = getButtonType(e.target as HTMLElement);

  if (buttonType === 'clear') {
    onInputClear();
    openDropdown(false);
  } else if (buttonType === 'open') {
    if (disableDropdown) return;
    openDropdown(!open.value);
  }
}

function onButtonKeydown(e: KeyboardEvent): void {
  if ((e.key === 'Enter' || e.key === ' ') && hasSelection.value) {
    e.preventDefault();
    onInputClear();
  }
}

function onInputBlur(): void {
  emit('autocomplete:blur', query.value);

  if (closeOnInputBlurProp) {
    openDropdown(!closeOnInputBlur.value);

    closeOnInputBlur.value = true;
  }

  if (selected.value?.value && query.value !== selected.value.value && autoFocusFirstOption) {
    query.value = selected.value.value;
  }
}

function onInputClear(): void {
  selected.value = null;
  query.value = '';
}

function onInputClick(): void {
  if (disableDropdown) return;
  openDropdown(true);
}

function onInputFocus(): void {
  if (disableDropdown) return;
  openDropdown(true);
}

function onInputInput(e: Event): void {
  query.value = (e.target as HTMLInputElement).value;

  if (!disableDropdown) {
    open.value = shouldShowDropdown.value;
  }

  if (!autoFocusFirstOption) {
    currentOption.value = '';
  } else if (options.length > 0) {
    currentOption.value = options[0].key;
  }
}

function onInputKeyDown(e: KeyboardEvent): void {
  switch (e.key) {
    case 'ArrowDown':
      if (!shouldShowDropdown.value) {
        return;
      }

      if (!open.value) {
        open.value = true;
      }

      if (currentIndex.value === -1 && options.length > 0) {
        currentOption.value = options[0].key;
      } else if (currentIndex.value < options.length - 1) {
        currentOption.value = options[currentIndex.value + 1].key;
      }
      break;
    case 'ArrowUp':
      if (!shouldShowDropdown.value) {
        return;
      }

      if (currentIndex.value > 0) {
        currentOption.value = options[currentIndex.value - 1].key;
      }
      break;
    case 'Enter':
      if (!open.value || currentIndex.value === -1) {
        onPressEnter(query.value);
        return;
      }
      onOptionSelect(options[currentIndex.value]);
      break;
    case 'Escape':
      if (open.value) {
        e.stopPropagation();
        open.value = false;
      } else {
        onInputClear();
      }
      break;
    default:
      return;
  }

  e.preventDefault();
}

function setValue(value: string): void {
  query.value = value;
}

watch(
  () => options,
  (newValue, oldValue) => {
    if (isEqual(newValue, oldValue)) {
      return;
    }

    if (autoFocusFirstOption) {
      currentOption.value = newValue[0]?.key ?? '';
    } else {
      currentOption.value = '';
    }
  },
  {
    immediate: true,
  },
);

watch(query, (newValue, oldValue) => {
  emit('autocomplete:query', newValue);

  if (newValue === '' && oldValue !== '') {
    onInputClear();
  }
});

watch(selected, (newValue) => {
  emit('autocomplete:change', newValue);
});

defineExpose<ExposedPhxsgAutocompleteMethod>({ setValue });
</script>

<template>
  <div :class="rootClasses">
    <div ref="rootRef">
      <slot name="field" v-bind="fieldProps">
        <PhxsgAutocompleteField
          :error="error"
          :field-props="fieldProps"
          :has-selection="hasSelection"
          :has-value="hasValue"
          :clear-button-label="clearButtonLabel"
          :clear-button-icon="clearButtonIcon"
          :open-button-label="openButtonLabel"
          :open-button-icon="openButtonIcon"
          :display-both-buttons="displayBothButtons"
          :always-display-clear-button="alwaysDisplayClearButton"
        />
      </slot>

      <PhxsgAutocompleteDropdown
        v-if="!disableDropdown"
        :id="dropdownId"
        :root-ref="rootRef"
        :options="options"
        :current-option="currentOption"
        :open="isDropdownOpen"
        :label="label"
        :max-rows="maxRows"
        :min-rows="minRows"
        :no-result-text="noResultText"
        :loading-text="loadingText"
        :query="query"
        :loading="loading"
        :teleport-target="teleportTarget"
        :dropdown-offset="dropdownOffset"
        @option-select="onOptionSelect"
        @option-action="onOptionAction"
      >
        <template v-if="$slots.option" #option="scope">
          <slot name="option" v-bind="scope" />
        </template>

        <template v-if="$slots.empty" #empty="scope">
          <slot name="empty" v-bind="scope" />
        </template>

        <template v-if="$slots.loading" #loading="scope">
          <slot name="loading" v-bind="scope" />
        </template>
      </PhxsgAutocompleteDropdown>
    </div>
  </div>
</template>

<style src="./autocomplete.css" />
